import { useCallback, useId, useMemo } from 'react'
import { formatCurrency, digitsToCurrency } from './utils'
import styles from './CurrencyInput.module.css'

export interface CurrencyInputProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'type' | 'inputMode'> {
  /** Valor numérico (string ou number) - será formatado automaticamente */
  value?: string | number | null
  /** Callback quando o valor muda - recebe o valor numérico (string) */
  onChange?: (value: string) => void
  /** Se deve tratar entrada como centavos (padrão: true) */
  inputAsCents?: boolean
  /** Classe CSS adicional */
  className?: string
}

/**
 * Componente de input para valores monetários em Real brasileiro (R$)
 * 
 * @example
 * ```tsx
 * <CurrencyInput
 *   value={price}
 *   onChange={(value) => setPrice(value)}
 *   placeholder="R$ 0,00"
 * />
 * ```
 */
export function CurrencyInput({
  value: valueProp,
  onChange,
  inputAsCents = true,
  className,
  id: idProp,
  placeholder = 'R$ 0,00',
  onFocus,
  onBlur,
  ...rest
}: CurrencyInputProps) {
  const id = useId()
  const inputId = idProp ?? id
  
  // Calcula o valor de exibição formatado
  const displayValue = useMemo(() => {
    if (!valueProp && valueProp !== 0) return ''
    return formatCurrency(valueProp)
  }, [valueProp])
  
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      
      // Extrai apenas dígitos da entrada
      const digits = rawValue.replace(/\D/g, '')
      
      if (!digits) {
        onChange?.('')
        return
      }
      
      // Converte dígitos para valor monetário
      const currencyValue = inputAsCents ? digitsToCurrency(digits) : digits
      
      // Chama onChange com valor numérico (string)
      onChange?.(currencyValue)
    },
    [onChange, inputAsCents]
  )
  
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Formatação é garantida pelo useMemo do displayValue
      onBlur?.(e)
    },
    [onBlur]
  )
  
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Seleciona todo o texto para facilitar substituição
      e.target.select()
      onFocus?.(e)
    },
    [onFocus]
  )
  
  return (
    <input
      {...rest}
      id={inputId}
      type="text"
      inputMode="decimal"
      className={[styles.currencyInput, className].filter(Boolean).join(' ')}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  )
}
